import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/components/quote";

// Encapsulates live quote + quantity-break calculation for the Quote Builder.
// Owns its own result state and re-runs (debounced) whenever the job changes.
export function useQuoteCalculation(job) {
  const [quote, setQuote] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [quantityBreakResults, setQuantityBreakResults] = useState(null);
  const [isCalculatingQtyBreak, setIsCalculatingQtyBreak] = useState(false);

  const calculateQuote = useCallback(async () => {
    if (job.garments.length === 0) {
      setQuote(null);
      return;
    }

    setIsCalculating(true);
    try {
      const res = await axios.post(`${API}/quote/calculate`, job);
      setQuote(res.data);
    } catch (error) {
      console.error("Quote calculation failed", error);
      toast.error("Failed to calculate quote");
    } finally {
      setIsCalculating(false);
    }
  }, [job]);

  useEffect(() => {
    const timer = setTimeout(() => {
      calculateQuote();
    }, 300);
    return () => clearTimeout(timer);
  }, [calculateQuote]);

  const calculateQuantityBreak = useCallback(async () => {
    if (!job.is_quantity_break || !job.quantity_break_settings?.length || job.garments.length === 0) {
      setQuantityBreakResults(null);
      return;
    }

    setIsCalculatingQtyBreak(true);
    try {
      const res = await axios.post(`${API}/quote/calculate-quantity-break`, job);
      setQuantityBreakResults(res.data);
    } catch (error) {
      console.error("Quantity break calculation failed", error);
    } finally {
      setIsCalculatingQtyBreak(false);
    }
  }, [job]);

  useEffect(() => {
    if (job.is_quantity_break) {
      const timer = setTimeout(() => {
        calculateQuantityBreak();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [job.is_quantity_break, job.quantity_break_settings, job.garments, job.artwork_groups, calculateQuantityBreak]);

  return { quote, setQuote, isCalculating, quantityBreakResults, isCalculatingQtyBreak };
}
